import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Download, ArrowLeft, CheckCircle2, Loader2, Smartphone, Share, Plus, X } from "lucide-react";

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export default function TicketPass() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");
  const [showInstall, setShowInstall] = useState(false);
  const promptRef = useRef(null);

  useEffect(() => {
    api.get(`/tickets/${id}/detail`).then((r) => setT(r.data))
      .catch((e) => setErr(e.response?.status === 403 ? "This isn't your ticket." : "Ticket not found."));
  }, [id]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); promptRef.current = e; };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const addToHome = async () => {
    if (promptRef.current) {
      promptRef.current.prompt();
      await promptRef.current.userChoice;
      promptRef.current = null;
    } else {
      setShowInstall(true);
    }
  };

  const downloadPdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${id}/pass.pdf`, "_blank");

  let pretty = "";
  if (t?.event_date) { try { pretty = new Date(t.event_date).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { pretty = t.event_date; } }

  return (
    <div className="min-h-screen bg-navy grain flex flex-col items-center justify-center px-6 py-16" data-testid="ticket-pass">
      <Link to="/supporter" className="self-start text-bone/60 hover:text-gold transition-colors flex items-center gap-2 font-mono text-xs uppercase tracking-widest mb-8">
        <ArrowLeft size={14} /> Back
      </Link>
      {err && <p className="text-bone/70 font-sans" data-testid="ticket-error">{err}</p>}
      {!t && !err && <Loader2 className="animate-spin text-gold" size={32} />}
      {t && (
        <div className="w-full max-w-sm bg-bone shadow-2xl overflow-hidden">
          <div className="bg-navy text-center py-5 border-b-2 border-dashed border-bone/20">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">In His Image Movement Ministries</span>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-bone mt-1">Admit One</div>
          </div>
          <div className="p-8 text-center">
            <h1 className="font-serif text-3xl tracking-tight" data-testid="pass-title">{t.event_title}</h1>
            <p className="font-sans text-sm text-charcoal/60 mt-2">{pretty}</p>
            <div className="my-7 flex justify-center">
              <div className="p-3 border border-charcoal/10 bg-white">
                <img src={t.qr_url} alt="Ticket QR" className="w-52 h-52" data-testid="pass-qr" />
              </div>
            </div>
            <p className="font-serif text-xl">{t.user_name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mt-1">{t.quantity} Ticket{t.quantity === 1 ? "" : "s"}</p>
            {t.checked_in && (
              <p className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-olive"><CheckCircle2 size={13} /> Checked In</p>
            )}
          </div>
          <div className="border-t-2 border-dashed border-charcoal/15 p-5 space-y-3">
            <button onClick={addToHome} data-testid="add-to-home-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-navy hover:text-bone transition-colors">
              <Smartphone size={15} /> Add to Home Screen
            </button>
            <button onClick={downloadPdf} data-testid="download-pass-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-charcoal text-bone py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
              <Download size={15} /> Save PDF Pass
            </button>
            <p className="font-sans text-[11px] text-charcoal/40 text-center">One tap on your home screen opens this QR instantly.</p>
          </div>
        </div>
      )}

      {showInstall && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4" data-testid="install-sheet">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => setShowInstall(false)} />
          <div className="relative bg-bone w-full max-w-sm p-8 border border-gold/40">
            <button onClick={() => setShowInstall(false)} data-testid="install-close" className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            <Smartphone size={30} className="text-gold" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl tracking-tight mt-4">Add this ticket to your home screen</h3>
            {isIOS() ? (
              <ol className="mt-5 space-y-4 font-sans text-sm text-charcoal/75">
                <li className="flex items-center gap-3"><span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">1</span> Tap the <Share size={15} className="inline text-navy" /> Share button in Safari's toolbar.</li>
                <li className="flex items-center gap-3"><span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">2</span> Choose <Plus size={15} className="inline text-navy" /> "Add to Home Screen".</li>
                <li className="flex items-center gap-3"><span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">3</span> Tap the icon anytime to open your QR.</li>
              </ol>
            ) : (
              <ol className="mt-5 space-y-4 font-sans text-sm text-charcoal/75">
                <li className="flex items-center gap-3"><span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">1</span> Open your browser menu (⋮ in the top corner).</li>
                <li className="flex items-center gap-3"><span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">2</span> Tap "Add to Home screen" (or "Install app").</li>
                <li className="flex items-center gap-3"><span className="w-6 h-6 bg-navy text-bone flex items-center justify-center font-mono text-xs shrink-0">3</span> Tap the icon anytime to open your QR.</li>
              </ol>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
